import React from "react";
import AlertsDashboard from "./AlertsDashboard";
import AlertsList from "./AlertsList";
import AlertDetail from "./AlertDetail";
import ScamActivityMap from "./ScamActivityMap";
import RefreshButton from "../common/RefreshButton";

/**
 * AlertsIntelligenceHubDesktop - Desktop shell for the Alerts Intelligence Hub.
 *
 * Renders the desktop-optimised header:
 *  - Back button row (when not on dashboard)
 *  - Title + subtitle on the left, search icon/input + refresh on the right
 *
 * All state and event handlers are owned by AlertsIntelligenceHub.js and
 * passed in as props — this component is a pure presentation layer.
 */
function AlertsIntelligenceHubDesktop({
  // View
  currentView,
  selectedCategory,
  categories,
  filterLabel,
  // Search
  searchExpanded,
  searchInput,
  activeSearchTerm,
  // Data
  dashboardData,
  dashboardLoading,
  dashboardError,
  alerts,
  alertsLoading,
  alertsPagination,
  selectedAlert,
  alertDetail,
  detailLoading,
  // Handlers
  goBack,
  handleViewAlert,
  handleCategorySelect,
  handleMarkReviewed,
  handleStartTraining,
  handleOpenShareModal,
  handleDismissAlert,
  handleRefresh,
  loadMoreAlerts,
  navigateTo,
  toggleSearch,
  handleSearch,
  handleSearchKeyPress,
  handleSearchBlur,
  clearSearch,
  setSearchInput,
}) {
  const pageTitle =
    currentView === "dashboard"
      ? "Alerts Intelligence Hub"
      : currentView === "scammap"
        ? "Scam Activity Near You"
        : currentView === "detail"
          ? "Alert Details"
          : currentView === "list"
            ? filterLabel || "All Alerts"
            : currentView === "category" && selectedCategory
              ? categories.find((c) => c.name === selectedCategory)
                  ?.displayName || selectedCategory
              : "";

  const pageSubtitle =
    currentView === "dashboard"
      ? "Stay informed about the latest scam threats and protect yourself with actionable intelligence"
      : currentView === "scammap"
        ? "Search for reported scams near any address using BBB Scam Tracker data"
        : currentView === "detail"
          ? "View detailed information about this alert"
          : currentView === "list"
            ? filterLabel
              ? `Showing ${filterLabel.toLowerCase()} alerts`
              : "Browse all available alerts"
            : currentView === "category"
              ? "Alerts in this category"
              : "";

  return (
    <div className="common-container aih-container">
      {/* ── Desktop Header — hidden for scammap (that view owns its header) ── */}
      {currentView !== "scammap" && (
        <div className="common-header">
          <div className="aih-header-wrapper">
            {/* Row 1: Back button (when not on dashboard) */}
            {currentView !== "dashboard" && (
              <div className="aih-header-back-row">
                <button
                  className="aih-back-button"
                  onClick={goBack}
                  aria-label="Go back"
                >
                  <span className="back-arrow">←</span>
                  <span className="back-text">
                    {currentView === "detail"
                      ? "Back to Alerts"
                      : "Back to Alerts Hub"}
                  </span>
                </button>
              </div>
            )}

            {/* Row 2: Title + search + refresh */}
            <div className="aih-header-content">
              <div className="aih-title-section">
                <h1 className="main-title">{pageTitle}</h1>
                <p className="common-subtitle">{pageSubtitle}</p>
              </div>

              <div className="aih-header-actions">
                {/* Search */}
                <div
                  className={`aih-search-container ${searchExpanded ? "expanded" : ""}`}
                >
                  {searchExpanded ? (
                    <>
                      <div className="aih-search-row">
                        <input
                          type="text"
                          className="aih-search-input"
                          placeholder="Search alerts..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={handleSearchKeyPress}
                          onBlur={handleSearchBlur}
                          autoFocus
                        />
                        <button
                          className="aih-search-submit"
                          onClick={handleSearch}
                          aria-label="Search"
                        >
                          Search
                        </button>
                      </div>
                      {activeSearchTerm && (
                        <div className="aih-search-criteria">
                          <span className="aih-search-criteria-label">
                            Search:
                          </span>
                          <span className="aih-search-criteria-term">
                            {activeSearchTerm}
                          </span>
                          <button
                            className="aih-search-criteria-clear"
                            onClick={clearSearch}
                            aria-label="Clear search"
                            title="Clear search and show all alerts"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      className="aih-search-icon-btn"
                      onClick={toggleSearch}
                      aria-label="Open search"
                      title="Search alerts"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    </button>
                  )}
                </div>

                <RefreshButton onClick={handleRefresh} title="Refresh data" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Content Panel ── */}
      <div className="aih-content-panel">
        {currentView === "dashboard" && (
          <AlertsDashboard
            data={dashboardData}
            loading={dashboardLoading}
            error={dashboardError}
            categories={categories}
            isMobile={false}
            onViewAlert={handleViewAlert}
            onCategorySelect={handleCategorySelect}
            onRefresh={handleRefresh}
            onViewAllAlerts={() => navigateTo("list")}
            onFilterCritical={() =>
              navigateTo("list", {
                minThreatLevel: 8,
                maxThreatLevel: 10,
                filterLabel: "Critical Alerts",
              })
            }
            onFilterUnreviewed={() =>
              navigateTo("list", { unreviewedOnly: true })
            }
            onNavigateToScamMap={() => navigateTo("scammap")}
          />
        )}

        {currentView === "scammap" && <ScamActivityMap goBack={goBack} />}

        {(currentView === "list" || currentView === "category") && (
          <div className="aih-list-view">
            <AlertsList
              alerts={alerts}
              loading={alertsLoading}
              pagination={alertsPagination}
              onViewAlert={handleViewAlert}
              onMarkReviewed={handleMarkReviewed}
              onShare={handleOpenShareModal}
              onDismiss={handleDismissAlert}
              onLoadMore={loadMoreAlerts}
              emptyMessage={
                currentView === "category"
                  ? "No alerts found in this category."
                  : "No alerts available."
              }
            />
          </div>
        )}

        {currentView === "detail" && (
          <AlertDetail
            alertId={selectedAlert}
            alertData={alertDetail}
            loading={detailLoading}
            isMobile={false}
            onMarkReviewed={handleMarkReviewed}
            onStartTraining={handleStartTraining}
            onShare={handleOpenShareModal}
            onViewRelated={handleViewAlert}
            onBack={goBack}
          />
        )}
      </div>
    </div>
  );
}

export default AlertsIntelligenceHubDesktop;
